import { useState, useCallback } from 'react';
import { css } from '@emotion/react';
import { useAppShell } from '@/hooks/useAppShell';
import { useSideNav } from '@/hooks/useSideNav';
import { useNavPage } from '@/hooks/useNavPage';

const navWrapper = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 96px;
  padding: 0 40px;
  background: #fff;
  box-shadow: 0px 4px 15px 0px rgba(0, 0, 0, 0.02);
  overflow: hidden;
  position: relative;
  z-index: 10;

  @media (max-width: 1169px) {
    height: 56px;
    padding: 0 24px;
  }
`;

const navWrapperShell = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  padding: 0 16px;
  background: transparent;
  overflow: hidden;
  position: relative;
  z-index: 10;

  @media (max-width: 1169px) {
    padding: 0 24px;
  }
`;

const logoStyles = css`
  width: 160px;
  flex-shrink: 0;

  @media (max-width: 767px) {
    width: 80px;

    svg {
      height: 10px;
    }
  }
`;

const navItems = css`
  display: flex;
  align-items: center;
  gap: 24px;

  @media (max-width: 1169px) {
    display: none;
  }
`;

const navDropdown = css`
  display: none;

  @media (max-width: 1169px) {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 36px;
    padding: 0 8px;
    border-radius: 6px;
    background: transparent;
    border: none;
    cursor: pointer;
    font-family: 'FlodeskSans', sans-serif;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    color: #10100f;
    white-space: nowrap;
  }
`;

const navLink = css`
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.16px;
  color: rgba(61, 46, 41, 0.52);
  white-space: nowrap;
  cursor: pointer;
  text-decoration: none;
  transition: color var(--grn-transition-fast, 200ms) ease;

  &:hover {
    color: #121212;
  }
`;

const navLinkActive = css`
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0.16px;
  white-space: nowrap;
  cursor: pointer;
  text-decoration: none;
  color: #121212;
`;

const divider = css`
  width: 1px;
  height: 16px;
  background: rgba(55, 49, 47, 0.12);
`;

const profileSection = css`
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: flex-end;
  width: 160px;
  flex-shrink: 0;
  cursor: pointer;

  @media (max-width: 767px) {
    width: 80px;
  }
`;

const profileAvatar = css`
  width: 40px;
  height: 40px;
  border-radius: 60px;
  border: 1px solid rgba(55, 49, 47, 0.12);
  object-fit: cover;

  @media (max-width: 767px) {
    width: 32px;
    height: 32px;
  }
`;

const profileAvatarShell = css`
  width: 32px;
  height: 32px;
  border-radius: 60px;
  border: 1px solid rgba(55, 49, 47, 0.12);
  object-fit: cover;
`;

const chevronDown = css`
  width: 10px;
  height: 14px;
  color: rgba(61, 46, 41, 0.52);

  @media (max-width: 1169px) {
    display: none;
  }
`;

const dropdownChevron = css`
  color: var(--grn-color-icon, rgba(61, 46, 41, 0.52));
`;

const overlay = css`
  position: fixed;
  inset: 0;
  z-index: 100;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding-top: 80px;
`;

const closeButton = css`
  position: absolute;
  top: 28px;
  right: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--grn-color-icon, rgba(61, 46, 41, 0.52));
  transition: color var(--grn-transition-fast, 200ms) ease;

  &:hover {
    color: #121212;
  }
`;

const overlayNav = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
`;

const overlayLink = css`
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 500;
  font-size: 30px;
  line-height: 34.03px;
  color: rgba(61, 46, 41, 0.52);
  white-space: nowrap;
  cursor: pointer;
  text-decoration: none;
  background: none;
  border: none;
  padding: 0;
  transition: color var(--grn-transition-fast, 200ms) ease;

  &:hover {
    color: #121212;
  }
`;

const overlayLinkActive = css`
  font-family: 'FlodeskSans', sans-serif;
  font-weight: 500;
  font-size: 30px;
  line-height: 34.03px;
  color: #10100f;
  white-space: nowrap;
  cursor: pointer;
  text-decoration: none;
  background: none;
  border: none;
  padding: 0;
`;

const secondaryNav = css`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const iconButton = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 6px;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 8px;
  color: var(--grn-color-icon, rgba(57, 48, 45, 0.52));
  transition:
    color var(--grn-transition-fast, 200ms) ease,
    background var(--grn-transition-fast, 200ms) ease;

  &:hover {
    color: var(--grn-color-content, #121212);
    background: var(--grn-color-overlay, rgba(58, 47, 44, 0.08));
  }
`;

const iconButtonActive = css`
  color: var(--grn-color-content, #121212);
`;

const manageAndProfile = css`
  display: flex;
  align-items: center;
  gap: 24px;
  justify-content: flex-end;
  width: 160px;
  flex-shrink: 0;

  @media (max-width: 767px) {
    width: 80px;
  }
`;

export default function TopNav() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showDivider, setShowDivider] = useState(true);
  const [showHome] = useState(false);
  const [compactSecondary, setCompactSecondary] = useState(true);
  const [textSecondary] = useState(false);
  const [checkoutsIndex] = useState(0);
  const checkoutsNames = ['Checkouts', 'Sales pages', 'Pages'];
  const [isShell, setIsShell] = useAppShell();
  const [, setSideNav] = useSideNav();
  const [navPage, setNavPage] = useNavPage();

  const openMenu = useCallback(() => setIsMenuOpen(true), []);
  const closeMenu = useCallback(() => setIsMenuOpen(false), []);
  const toggleDivider = useCallback(() => setShowDivider(prev => !prev), []);
  const toggleShell = useCallback(() => setIsShell(prev => !prev), [setIsShell]);
  const toggleHome = useCallback(() => {
    setNavPage('emails');
  }, [setNavPage]);
  const toggleSideNav = useCallback(() => setSideNav(prev => !prev), [setSideNav]);
  const toggleCompactSecondary = useCallback(() => setCompactSecondary(prev => !prev), []);

  return (
    <>
      <nav css={isShell ? navWrapperShell : navWrapper}>
        <div
          css={[
            logoStyles,
            css`
              cursor: pointer;
            `,
          ]}
          role="button"
          tabIndex={0}
          onClick={toggleShell}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') toggleShell();
          }}
          aria-label="Toggle app shell"
        >
          <svg height="14" viewBox="0 0 57 12" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M14.3188 3.02031C16.9384 3.02031 18.8421 4.93639 18.8421 7.51821C18.8421 10.1001 16.9384 12 14.3188 12C11.6667 12 9.81182 10.1001 9.81182 7.51821C9.81182 4.93639 11.6667 3.02031 14.3188 3.02031ZM14.3188 4.74153C12.7568 4.74153 11.748 5.92696 11.748 7.502C11.748 9.12584 12.7568 10.295 14.3188 10.295C15.8971 10.295 16.8733 9.10952 16.8733 7.502C16.8733 5.94317 15.8808 4.74153 14.3188 4.74153Z"
              fill="#111110"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M35.1292 3.02031C37.944 3.02031 39.4735 5.08255 39.4735 7.59945C39.4735 7.7131 39.4735 7.92421 39.4573 8.08661H32.6235C32.6886 9.4993 33.8601 10.3599 35.2268 10.3599C36.1542 10.3599 36.9353 9.9377 37.4234 9.12586L39.3108 9.46685C38.7739 11.0906 36.984 11.9837 35.1455 11.9837C32.5096 11.9837 30.6873 10.1326 30.6873 7.51821C30.6873 4.96891 32.5259 3.02033 35.1292 3.02031ZM35.1292 4.61166C33.795 4.61167 32.8512 5.43991 32.656 6.60902H37.4559C37.2769 5.35866 36.4471 4.61166 35.1292 4.61166Z"
              fill="#111110"
            />
            <path
              d="M44.0942 2.9878C45.9002 2.9878 47.1695 3.97828 47.2996 5.55337L45.461 5.6184C45.3146 4.98506 44.8101 4.53042 43.9966 4.53042C43.1994 4.53042 42.7275 4.9201 42.7275 5.47213C42.7275 5.92685 43.0041 6.13794 43.6875 6.3653L45.3308 6.9336C46.6975 7.40454 47.4949 7.9566 47.4949 9.25564C47.4949 11.0256 46.0467 11.9836 44.0942 11.9836C42.1254 11.9836 40.7749 11.0256 40.6448 9.33679L42.581 9.25564C42.6299 9.88892 43.1505 10.4735 44.1105 10.4735C45.0054 10.4735 45.4934 10.0513 45.4934 9.46676C45.4934 8.89844 45.1193 8.70355 44.2081 8.39508L43.0041 8.00528C41.6211 7.56688 40.775 6.90115 40.775 5.63461C40.775 4.0433 42.1091 2.9878 44.0942 2.9878Z"
              fill="#111110"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M28.9135 11.7721H27.1074V11.155C27.1074 10.9276 27.1074 10.749 27.14 10.6029H27.0749C26.489 11.4635 25.4478 11.9669 24.2112 11.9669C21.7706 11.9669 20.0947 10.0833 20.0947 7.53396C20.0947 4.98458 21.7705 3.03599 24.1298 3.03596C25.3827 3.03596 26.4241 3.57189 26.9772 4.35136H27.0261C27.0098 3.45824 26.9935 3.23089 26.9935 2.22413V0.0481693H28.9135V11.7721ZM24.5691 4.77349C23.0072 4.7735 22.0309 6.00759 22.0309 7.51766C22.0309 9.06033 23.0234 10.2781 24.5691 10.2781C26.0986 10.2781 27.1074 9.07655 27.1074 7.50145C27.1074 5.95883 26.0986 4.77349 24.5691 4.77349Z"
              fill="#111110"
            />
            <path
              d="M4.03511 0C4.21413 0 4.39312 4.0185e-05 4.60462 0.0325143V1.70501C4.53955 1.68875 4.49066 1.6888 4.40935 1.6888C3.69348 1.68881 3.22165 1.93235 3.12406 2.6143C3.10776 2.76049 3.10776 2.85791 3.10776 2.98789V3.23142H4.60462V4.85519H3.12406V11.7726H1.20405V4.85519H0V3.23142H1.20405V2.93916C1.20405 1.10429 2.03387 1.08386e-05 4.03511 0Z"
              fill="#111110"
            />
            <path d="M8.03733 11.772H6.08481V0.0481693H8.03733V11.772Z" fill="#111110" />
            <path
              d="M51.1215 2.67868C51.1215 4.10764 51.089 5.00074 51.0727 6.26729L51.089 6.2836C51.691 5.63411 52.2442 5.14693 52.8624 4.51365L54.1153 3.23087H56.7675L52.2279 7.25782L56.9139 11.772H54.1642C53.0903 10.7003 52.0489 9.57995 51.1053 8.52449H51.089C51.1053 9.23892 51.1215 9.82351 51.1215 10.6353V11.772H49.2179V0.0481693H51.1215V2.67868Z"
              fill="#111110"
            />
          </svg>
        </div>

        <div css={navItems}>
          {showHome && (
            <>
              <span css={navLink}>Home</span>
              <div css={divider} />
            </>
          )}
          <span
            css={navPage === 'emails' ? navLinkActive : navLink}
            role="button"
            tabIndex={0}
            onClick={toggleHome}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') toggleHome();
            }}
          >
            Emails
          </span>
          <span css={navLink}>Forms</span>
          <span
            css={navLink}
            role="button"
            tabIndex={0}
            onClick={toggleDivider}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') toggleDivider();
            }}
          >
            Workflows
          </span>
          <span css={navLink}>{checkoutsNames[checkoutsIndex]}</span>
          {!compactSecondary && !textSecondary && (
            <>
              {showDivider && <div css={divider} />}
              <span
                css={navLink}
                role="button"
                tabIndex={0}
                onClick={toggleCompactSecondary}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') toggleCompactSecondary();
                }}
              >
                Products
              </span>
              <span css={navLink}>Audience</span>
              <span css={navLink}>Analytics</span>
            </>
          )}
        </div>

        <button css={navDropdown} type="button" onClick={openMenu}>
          Emails
          <svg css={dropdownChevron} width="10" height="18" viewBox="0 0 10 18" fill="none">
            <path
              d="M1 7L5 11L9 7"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div css={compactSecondary || textSecondary ? manageAndProfile : profileSection}>
          {textSecondary && (
            <div
              css={css`
                display: flex;
                align-items: center;
                gap: 24px;
              `}
            >
              <span css={navLink}>Products</span>
              <span css={navLink}>Audience</span>
              <span css={navLink}>Analytics</span>
            </div>
          )}
          {compactSecondary && (
            <div css={secondaryNav}>
              {/* Products - Bag icon */}
              <button
                css={[
                  iconButton,
                  (navPage === 'products' || navPage === 'discounts') && iconButtonActive,
                ]}
                type="button"
                onClick={() => setNavPage('products')}
                aria-label="Products"
              >
                <svg width="14" height="16" viewBox="0 0 14 16" fill="none">
                  <path
                    d="M7.00696 0C8.8708 -3.22115e-07 10.4369 1.27476 10.881 3H11.4093C11.6673 3 11.8981 2.99941 12.089 3.01465C12.2874 3.03056 12.4954 3.06673 12.6993 3.16699C12.9979 3.314 13.2448 3.54932 13.4054 3.84082C13.5151 4.04 13.56 4.24578 13.5851 4.44336C13.6092 4.63358 13.6201 4.86484 13.632 5.12305L13.8712 10.3252C13.9109 11.1891 13.9425 11.8755 13.922 12.4287C13.9012 12.9889 13.8258 13.471 13.6173 13.915C13.2822 14.6283 12.7236 15.2136 12.0265 15.5811C11.5926 15.8096 11.1142 15.9076 10.5558 15.9541C10.0041 16 9.31603 15.999 8.45129 15.999H5.5636C4.6988 15.999 4.01085 16 3.45911 15.9541C2.90043 15.9076 2.42146 15.8098 1.98743 15.5811C1.29033 15.2134 0.731558 14.6284 0.396607 13.915C0.188095 13.4708 0.112569 12.9881 0.0919195 12.4277C0.0715693 11.8745 0.103746 11.188 0.143677 10.3242L0.384888 5.12207C0.396815 4.86406 0.406694 4.63346 0.430787 4.44336C0.455839 4.24587 0.500893 4.03992 0.610474 3.84082C0.771034 3.54945 1.0181 3.314 1.31653 3.16699C1.52048 3.06664 1.72843 3.03058 1.92688 3.01465C2.11785 2.99936 2.34932 3 2.60754 3H3.13294C3.57689 1.2748 5.14323 0.00018437 7.00696 0ZM2.60754 4.25C2.3293 4.25 2.15604 4.25037 2.02649 4.26074C1.90601 4.27044 1.87338 4.28559 1.86829 4.28809C1.79964 4.32201 1.74315 4.37727 1.70618 4.44434C1.70304 4.45003 1.68606 4.48212 1.67102 4.60059C1.65468 4.72953 1.64579 4.90172 1.63294 5.17969L1.3927 10.3818C1.35179 11.2668 1.32294 11.8926 1.34094 12.3818C1.35872 12.8641 1.42162 13.1562 1.52844 13.3838C1.7479 13.8511 2.11375 14.2348 2.57044 14.4756C2.79281 14.5928 3.08191 14.668 3.56262 14.708C4.05054 14.7486 4.67766 14.749 5.5636 14.749H8.45129C9.33709 14.749 9.96443 14.7486 10.4523 14.708C10.9325 14.668 11.2212 14.5926 11.4435 14.4756C11.9002 14.2349 12.2659 13.8511 12.4855 13.3838C12.5923 13.1563 12.6551 12.8648 12.673 12.3828C12.6911 11.8936 12.6629 11.2677 12.6222 10.3828L12.3829 5.18066C12.3701 4.90238 12.3622 4.72963 12.3458 4.60059C12.3307 4.48142 12.3137 4.44978 12.3107 4.44434C12.2736 4.3771 12.2165 4.32201 12.1476 4.28809C12.1414 4.28508 12.1076 4.27022 11.9894 4.26074C11.8599 4.25041 11.6873 4.25 11.4093 4.25H11.007V5.375C11.0069 5.72012 10.7271 6 10.382 6C10.037 5.9998 9.75702 5.72 9.75696 5.375V4.25H4.25696V5.375C4.25689 5.72012 3.9771 6 3.63196 6C3.28699 5.9998 3.00702 5.72 3.00696 5.375V4.25H2.60754ZM7.00696 1.25C5.84144 1.25016 4.84759 1.97619 4.44739 3H9.56751C9.16729 1.97603 8.17268 1.25 7.00696 1.25Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
              {/* Audience - People icon */}
              <button
                css={[iconButton, navPage === 'audience' && iconButtonActive]}
                type="button"
                onClick={() => setNavPage('audience')}
                aria-label="Audience"
              >
                <svg width="15" height="16" viewBox="0 0 15 16" fill="none">
                  <path
                    d="M5.5752 8.5C6.40487 8.5 7.06511 8.49954 7.59668 8.54297C8.13542 8.58701 8.59695 8.67969 9.02051 8.89551C9.7026 9.24305 10.257 9.7974 10.6045 10.4795C10.8203 10.903 10.913 11.3646 10.957 11.9033C11.0005 12.4349 11 13.0951 11 13.9248V14.875C11 15.2202 10.7202 15.5 10.375 15.5C10.0298 15.5 9.75 15.2202 9.75 14.875V13.9248C9.75 13.0747 9.74922 12.4747 9.71094 12.0059C9.67323 11.5443 9.60229 11.2649 9.49121 11.0469C9.26351 10.6 8.90001 10.2365 8.45313 10.0088C8.23507 9.89771 7.95567 9.82677 7.49414 9.78906C7.02532 9.75078 6.42528 9.75 5.5752 9.75H5.4248C4.57472 9.75 3.97468 9.75078 3.50586 9.78906C3.04433 9.82677 2.76493 9.89771 2.54688 10.0088C2.09999 10.2365 1.73649 10.6 1.50879 11.0469C1.39771 11.2649 1.32677 11.5443 1.28906 12.0059C1.25078 12.4747 1.25 13.0747 1.25 13.9248V14.875C1.25 15.2202 0.970178 15.5 0.625 15.5C0.279822 15.5 2.47447e-07 15.2202 2.03912e-07 14.875V13.9248C1.50075e-07 13.0951 -0.000461942 12.4349 0.042969 11.9033C0.087012 11.3646 0.179695 10.903 0.395508 10.4795C0.743049 9.7974 1.2974 9.24305 1.97949 8.89551C2.40305 8.67969 2.86458 8.58701 3.40332 8.54297C3.93489 8.49954 4.59513 8.5 5.4248 8.5H5.5752ZM12.0547 9.14941C12.1932 8.83355 12.5619 8.68989 12.8779 8.82812C12.9262 8.84924 12.9737 8.87166 13.0205 8.89551C13.7025 9.24302 14.2569 9.7975 14.6045 10.4795C14.8203 10.9031 14.913 11.3655 14.957 11.9043C15.0004 12.4358 15 13.0963 15 13.9258V14.875C14.9998 15.2199 14.72 15.4999 14.375 15.5C14.0301 15.4999 13.7502 15.2199 13.75 14.875V13.9258C13.75 13.0756 13.7492 12.4747 13.7109 12.0059C13.6732 11.5444 13.6023 11.2649 13.4912 11.0469C13.2635 10.6 12.9 10.2365 12.4531 10.0088C12.4283 9.99612 12.4021 9.9841 12.376 9.97266C12.0603 9.83416 11.9167 9.46537 12.0547 9.14941ZM5.5 0.5C7.43293 0.5 9 2.06695 9 4C9 5.93305 7.43293 7.5 5.5 7.5C3.56697 7.5 2 5.93304 2 4C2 2.06696 3.56697 0.500002 5.5 0.5ZM10.0508 1.10742C10.1856 0.789763 10.5524 0.641627 10.8701 0.776367C12.1236 1.30814 12.9999 2.55155 13 4C13 5.44636 12.1224 6.6894 10.873 7.22363C10.5559 7.35895 10.1885 7.21151 10.0527 6.89453C9.91708 6.57723 10.0646 6.21 10.3818 6.07422C11.1871 5.72989 11.75 4.92877 11.75 4C11.7499 3.06779 11.187 2.26833 10.3818 1.92676C10.0642 1.79193 9.91605 1.42514 10.0508 1.10742ZM5.5 1.75C4.25732 1.75 3.25 2.75732 3.25 4C3.25 5.24268 4.25732 6.25 5.5 6.25C6.7426 6.25 7.75 5.24267 7.75 4C7.75 2.75733 6.7426 1.75 5.5 1.75Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
              {/* Analytics - Chart icon */}
              <button
                css={[iconButton, navPage === 'analytics' && iconButtonActive]}
                type="button"
                onClick={() => setNavPage('analytics')}
                aria-label="Analytics"
              >
                <svg width="17" height="18" viewBox="0 0 17 18" fill="none">
                  <path
                    d="M9.0918 0.583008C10.3411 0.583008 11.3161 0.582664 12.0977 0.646484C12.8865 0.710937 13.5342 0.84413 14.1182 1.1416C15.0822 1.63281 15.8661 2.41696 16.3574 3.38086C16.655 3.96482 16.7881 4.61258 16.8525 5.40137C16.9164 6.18304 16.916 7.15855 16.916 8.4082V9.5918C16.916 10.8412 16.9164 11.8161 16.8525 12.5977C16.7881 13.3866 16.655 14.0341 16.3574 14.6182C15.8661 15.5823 15.0823 16.3661 14.1182 16.8574C13.5341 17.155 12.8866 17.2881 12.0977 17.3525C11.3161 17.4164 10.3412 17.416 9.0918 17.416H7.9082C6.65856 17.416 5.68304 17.4164 4.90137 17.3525C4.11258 17.2881 3.46482 17.155 2.88086 16.8574C1.91696 16.3661 1.13281 15.5822 0.641602 14.6182C0.34413 14.0342 0.210937 13.3865 0.146484 12.5977C0.0826636 11.8161 0.0830078 10.8411 0.0830079 9.5918V8.4082C0.0830078 7.15856 0.0826194 6.18304 0.146484 5.40137C0.210965 4.61254 0.344048 3.96484 0.641602 3.38086C1.13289 2.41691 1.91691 1.63289 2.88086 1.1416C3.46484 0.844049 4.11254 0.710965 4.90137 0.646484C5.68304 0.582619 6.65855 0.583008 7.9082 0.583008H9.0918ZM7.9082 1.83301C6.63794 1.83301 5.7219 1.83384 5.00293 1.89258C4.29192 1.95072 3.82751 2.06225 3.44922 2.25488C2.72019 2.62637 2.12637 3.22019 1.75488 3.94922C1.56225 4.32751 1.45072 4.79193 1.39258 5.50293C1.33384 6.2219 1.33301 7.13794 1.33301 8.4082V9.5918C1.33301 10.8618 1.33387 11.7772 1.39258 12.4961C1.45071 13.2075 1.56216 13.6724 1.75488 14.0508C2.12633 14.7798 2.72027 15.3726 3.44922 15.7441C3.82753 15.9368 4.29184 16.0493 5.00293 16.1074C5.7219 16.1662 6.63794 16.166 7.9082 16.166H9.0918C10.3618 16.166 11.2772 16.1661 11.9961 16.1074C12.7077 16.0493 13.1723 15.937 13.5508 15.7441C14.2798 15.3726 14.8726 14.7798 15.2441 14.0508C15.437 13.6723 15.5493 13.2077 15.6074 12.4961C15.6661 11.7772 15.666 10.8618 15.666 9.5918V8.4082C15.666 7.13794 15.6662 6.2219 15.6074 5.50293C15.5493 4.79184 15.4368 4.32753 15.2441 3.94922C14.8726 3.22027 14.2798 2.62633 13.5508 2.25488C13.1724 2.06216 12.7075 1.95071 11.9961 1.89258C11.2772 1.83387 10.3618 1.83301 9.0918 1.83301H7.9082ZM11.7588 6.82813C12.1038 6.82837 12.3838 7.1081 12.3838 7.45313V12.7979C12.3836 13.1427 12.1037 13.4226 11.7588 13.4229C11.4137 13.4229 11.1339 13.1429 11.1338 12.7979V7.45313C11.1338 7.10795 11.4136 6.82813 11.7588 6.82813ZM5.24121 9.07813C5.58639 9.07813 5.86621 9.35795 5.86621 9.70313V12.7969C5.86621 13.1421 5.58639 13.4219 5.24121 13.4219C4.89624 13.4216 4.61621 13.1419 4.61621 12.7969V9.70313C4.61621 9.3581 4.89624 9.07837 5.24121 9.07813ZM8.5 4.57813C8.84518 4.57813 9.125 4.85795 9.125 5.20313V12.7969C9.125 13.1421 8.84518 13.4219 8.5 13.4219C8.15482 13.4219 7.875 13.1421 7.875 12.7969V5.20313C7.875 4.85795 8.15482 4.57813 8.5 4.57813Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            </div>
          )}
          <div
            css={css`
              display: flex;
              align-items: center;
              gap: 8px;
              cursor: pointer;
            `}
            role="button"
            tabIndex={0}
            onClick={toggleSideNav}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') toggleSideNav();
            }}
          >
            <img
              css={isShell ? profileAvatarShell : profileAvatar}
              src="/images/profile.png"
              alt="Profile"
            />
            <svg css={chevronDown} viewBox="0 0 10 14" fill="none">
              <path
                d="M1 5L5 9L9 5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
      </nav>

      {isMenuOpen && (
        <div css={overlay} onClick={closeMenu} role="presentation">
          <button css={closeButton} type="button" onClick={closeMenu} aria-label="Close menu">
            <svg width="19" height="19" viewBox="0 0 19 19" fill="none">
              <path
                d="M1 1L18 18M18 1L1 18"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
          <div css={overlayNav}>
            <span css={overlayLink}>Home</span>
            <span css={overlayLinkActive}>Emails</span>
            <span css={overlayLink}>Forms</span>
            <span css={overlayLink}>Workflows</span>
            <span css={overlayLink}>Checkouts</span>
            <span css={overlayLink}>Products</span>
            <span css={overlayLink}>Audience</span>
            <span css={overlayLink}>Analytics</span>
          </div>
        </div>
      )}
    </>
  );
}
